import React from 'react';
import { Grid, Theme, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import { RegionWithCurrencyFragment } from 'src/generated/graphql';
import { ReactComponent as UndergroundIcon } from 'src/assets/images/Underground.svg';
import { ReactComponent as SubwayIcon } from 'src/assets/images/Subway.svg';
import { convertMetresToMiles } from 'src/libs/utils/size';

type UseStylesProps = {
    iconColor?: string;
    theme: Theme;
}

const useStyles = makeStyles({
    undergroundIcon: ({ iconColor }: UseStylesProps) => ({
        marginRight: '0.25rem',
        '& path, & circle': {
            stroke: iconColor,
        },
    }),
    subwayIcon: ({ iconColor }: UseStylesProps) => ({
        marginRight: '0.25rem',
        fontSize: '1.5rem',
        '& path': {
            fill: iconColor,
        },
    }),
    name: ({ theme }: UseStylesProps) => ({
        maxWidth: '75%',
        [theme.breakpoints.down(720)]: {
            maxWidth: '45%',
        },
    }),
    distance: {
        marginLeft: '0.125rem',
    },
    iconColor: ({ iconColor }: UseStylesProps) => ({
        '& path, & circle': {
            fill: iconColor,
        },
    }),
});

type TubeStationProps = {
    name?: string;
    distance?: number;
    duration?: string;
    regionId?: string;
    regions?: RegionWithCurrencyFragment[];
    iconColor?: string;
};

export const TubeStation = ({ name = '', distance, duration, regionId, regions, iconColor }: TubeStationProps) => {
    const theme = useTheme();
    const classes = useStyles({ iconColor, theme });
    let region: RegionWithCurrencyFragment | undefined;
    let isUK = false;
    let isUS = false;

    const mobileDevice = useMediaQuery(theme.breakpoints.down('xs'));

    if (regions?.length && regionId) {
        region = regions.find((item) => item.id === regionId);
        if (region && region.parentRegionId) {
            do {
                // eslint-disable-next-line no-loop-func
                region = regions.find((item) => item.id === region?.parentRegionId);
            }
            while (region?.parentRegionId);
        }

        isUK = region?.name.toLowerCase() === 'united kingdom';
        isUS = region?.name.toLowerCase() === 'united states';
    }

    if (name === '') {
        return null;
    }

    return (
        <Grid container alignItems="center" justifyContent="flex-start">
            {!isUS && <UndergroundIcon height="16" width="16" className={classes.undergroundIcon} />}
            {isUS && <SubwayIcon height="20" width="20" className={classes.subwayIcon} />}
            <Typography
                variant={mobileDevice ? 'caption' : 'body2'}
                color="primary"
                className={classes.name}
                noWrap
            >
                {name}
            </Typography>
            {!!(distance || duration) && (
                <Typography variant="caption" color="textSecondary" className={classes.distance}>
                    {distance
                        && !mobileDevice
                        && `(${isUK || isUS ? `${convertMetresToMiles(distance)} miles` : distance})`}
                    {duration && `(${duration} walk)`}
                </Typography>
            )}
        </Grid>
    );
};
